import { DropdownOption } from 'components/final-form/dropdownUtils';
import { generateRadioButtonOptionsFromEnum } from 'components/final-form/radioUtils';
import React, { Component } from "react";
import { MessageSourceProps, withMessages } from "react-message-source";
import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import axios from 'services/http';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { Button, ButtonGroup, Header, RadioProps } from 'semantic-ui-react';
import styled from 'styled-components';
import DatePicker from 'components/final-form/DatePicker';
import { noop } from 'utils/functionUtils';
import { DayStatisticRequest, DayStatisticResponse, PdfResponse, TariffDto } from 'ts-types/api.types';
import { FormApi, FormState } from 'final-form';
import { DayStatisticsOptions, ProceduresProtocolSortBy } from 'ts-types/api.enums';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import moment from 'moment';
import Select from 'components/final-form/Select';
import {
  getProceduresProtocolPdfUrl,
  getProceduresProtocolPrintOutput,
  getTurnOverOrPendingDayStatistic
} from 'services/dayStatisticSerivce';
import { ExtendedDayStatisticRequest } from 'components/statistic/day-statistic/DayStatisticView';
import Radio from 'components/final-form/Radio';
import PdfModal from 'components/pdf-modal/PdfModal';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;

  .ui.buttons {
    margin-left: 10px !important;
  }

  .field {
    display: inline-block !important;
    min-width: 12rem;
  }
`;

const StyledHeader = styled(Header)`
  display: inline-block;
`;

const StyledContainer = styled.div`

  flex: 1 1 auto;

  .ui.header:not(h1):not(h2):not(h3):not(h4):not(h5):not(h6) {
    font-size: 1.1rem;
    color: #373737;

    &.small {
      font-size: 1.05em;
    }
  }

  .ui.header.tiny {
    font-size: 1em !important;
  }
`;

const StyledLabel = styled.label`
  display: inline-block;
  min-width: 40px !important;
  margin-right: 10px;

  &.margin + .margin {
    margin-left: 10px;
  }
`;

const SearchOptionsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const SearchGroup = styled.div`
  flex: 0 1 auto;
  margin-left: 5rem;

  &.noMargin {
    margin-left: 0;
  }
`;

const TableContainer = styled.div`
  flex: 1 1 auto;
  min-height: 173px;
  min-width: 200px;
  max-width: 300px;
`;

const SearchButtonContainer = styled.div`
  flex: 1 1 100%;
`;

const SearchField = styled.div`
  margin-bottom: 7px;

  &.marginTop {
    margin-top: .5rem;
  }

  &.marginBottom {
    margin-bottom: 2rem;
  }
`;

interface Props extends MessageSourceProps, AuthConsumerRenderProps {
  onFilterStatistics: (dayStatisticData: DayStatisticResponse) => void,
  onStatisticsLoadingChange: (statisticsLoading: boolean) => void,
  searchData: Partial<ExtendedDayStatisticRequest>,
  setSearchData: (searchData: Partial<ExtendedDayStatisticRequest>) => void,
  tariffs: Array<TariffDto>,
  treaters: Array<DropdownOption>,
  proceduresProtocolPdfData?: PdfResponse,
  proceduresProtocolPdfModalOpen: boolean,
  setErrorMessage: (errorMessage?: string) => void
}

interface State {
  proceduresProtocolPdfData?: PdfResponse,
  proceduresProtocolPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource
}

class DayStatisticSearchView extends Component<Props, State> {

  radioMap: Map<string, RadioProps> = new Map<string, RadioProps>();

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {radioMap} = this;
    const {getMessage} = props;

    const sortByValues = Object.values(ProceduresProtocolSortBy);
    const sortBy = generateRadioButtonOptionsFromEnum(sortByValues, "sortBy", getMessage);
    sortBy.forEach((radioDefinition, index) => {
      radioMap.set(sortByValues[index], radioDefinition);
    });

    this.state = {
      proceduresProtocolPdfModalOpen: false,
      cancelTokenSource: cancelTokenSource
    };
  };

  handleChange = ({values}: FormState, form: FormApi) => {
    const fromDate = values.fromDate && new Date(values.fromDate);
    const toDate = values.toDate && new Date(values.toDate);

    if (fromDate && toDate && fromDate.getTime() > toDate.getTime()) {
      form.change("toDate", moment(fromDate).format("YYYY-MM-DD"));
    }

    const treaterSelection: boolean = values.treaterSelection;

    if (!treaterSelection) {
      form.change("treaterId", undefined);
    }
  };

  changeSearchOption = (option: DayStatisticsOptions, form: FormApi) => {
    form.change("option", option);

    if (DayStatisticsOptions.TURN_OVER === option) {
      form.change("billStatus", 1);
    }

    if (DayStatisticsOptions.PENDING_WORK === option) {
      form.change("billStatus", 0);
    }
  }

  getPdfUrl = (pdfResponse: PdfResponse) => {
    return getProceduresProtocolPdfUrl(pdfResponse.pdfId, pdfResponse.pdfName);
  }

  openProceduresProtocolPdfModal = (pdfResponse: PdfResponse) => {
    this.setState({
      proceduresProtocolPdfModalOpen: true,
      proceduresProtocolPdfData: pdfResponse
    })
  };

  closeProceduresProtocolPdfModal = () => {

    this.setState({
      proceduresProtocolPdfModalOpen: false,
      proceduresProtocolPdfData: undefined
    });
  };

  getStatistics = async (values: any) => {

    const {
      onStatisticsLoadingChange,
      onFilterStatistics,
      setSearchData,
      setErrorMessage,
      getMessage
    } = this.props;

    const {cancelTokenSource} = this.state;

    setSearchData(values);

    let request = {
      fromDate: values.fromDate,
      toDate: values.toDate,
      tariffIds: values.tariffIds,
      treaterId: values.treaterId,
      mandateId: values.mandateId,
      billStatus: values.billStatus,
      sortBy: values.sortBy,
      option: values.option
    };

    if (DayStatisticsOptions.PROCEDURES_PROTOCOL !== values.option) {
      onStatisticsLoadingChange(true);

      await getTurnOverOrPendingDayStatistic(request, cancelTokenSource)
          .then(response => {
            onFilterStatistics(response);
          })
          .catch((error) => {
            setErrorMessage(getMessage("error.general"));
          })
          .finally(() => {
            onStatisticsLoadingChange(false);
          })
    } else {
      await getProceduresProtocolPrintOutput(request, cancelTokenSource)
          .then(response => {
            this.openProceduresProtocolPdfModal(response);
          })
          .catch((error) => setErrorMessage(getMessage("error.general")));
    }
  };

  selectTariff = (values: DayStatisticRequest, form: FormApi, id: number) => {
    const tariffSelected = values.tariffIds.includes(id);
    if (tariffSelected) {
      const tariffs = values.tariffIds.filter((tariffId: number) => tariffId !== id);
      form.change("tariffIds", tariffs);
    } else {
      const tariffs = [...values.tariffIds, id];
      form.change("tariffIds", tariffs);
    }
  }

  onEnterKeyPressed = (e: any, values: any) => {
    if (e.keyCode === 13) {
      e.preventDefault();
      this.getStatistics(values)
    }
  };

  render(): React.ReactElement {
    const {searchData} = this.props;
    const {proceduresProtocolPdfModalOpen, proceduresProtocolPdfData} = this.state;
    return (
        <React.Fragment>
          <FinalForm
              onSubmit={() => noop}
              initialValues={searchData}
              subscription={{values: true, pristine: true}}
              render={this.renderForm}
          />
          {
            proceduresProtocolPdfModalOpen &&
            <PdfModal
                modalOpen={proceduresProtocolPdfModalOpen}
                onCloseModal={this.closeProceduresProtocolPdfModal}
                pdfResponses={[proceduresProtocolPdfData]}
                getPdfUrl={this.getPdfUrl}
            />
          }
        </React.Fragment>
    );
  }

  renderForm = ({handleSubmit, values, form}: FormRenderProps): React.ReactNode => {

    const {radioMap, props} = this;
    const {tariffs, treaters, getMessage} = props;

    const time = radioMap.get(ProceduresProtocolSortBy.TIME);
    const patientName = radioMap.get(ProceduresProtocolSortBy.PATIENT_NAME);

    const searchFilters = [
      DayStatisticsOptions.TURN_OVER,
      DayStatisticsOptions.PENDING_WORK,
      DayStatisticsOptions.PROCEDURES_PROTOCOL
    ];

    const treaterDisabled = !values.treaterSelection;
    const disabledBefore = values.fromDate && new Date(values.fromDate);

    const hasValidationErrors = form.getState().hasValidationErrors;
    const disableSearch = values.tariffIds === null || values.tariffIds.length < 1;

    const btnMessage = values.option === DayStatisticsOptions.PROCEDURES_PROTOCOL ? "output" : "search";

    return (
        <StyledForm onSubmit={handleSubmit}>
          <StyledContainer>
            <StyledHeader>{getMessage("searchOptions")}</StyledHeader>
            <ButtonGroup>
              {searchFilters.map(option => (
                      <Button key={option}
                              active={values.option === option}
                              onClick={() => this.changeSearchOption(option, form)}>
                        {getMessage(`dayStatisticOption.${option}`)}
                      </Button>
                  )
              )}
            </ButtonGroup>
          </StyledContainer>
          <StyledContainer style={{marginTop: 10}}
                           onKeyDown={(e) => this.onEnterKeyPressed(e, values)}>
            <SearchOptionsContainer>
              <SearchGroup className="noMargin">
                <Header size="tiny">{getMessage("billJournal.timePeriod")}</Header>
                <SearchField>
                  <StyledLabel className="margin">
                    {getMessage("from")}:
                  </StyledLabel>
                  <Field
                      name="fromDate"
                      component={DatePicker}
                      fluid
                  />
                </SearchField>
                <SearchField className="marginBottom">
                  <StyledLabel className="margin">
                    {getMessage("to")}:
                  </StyledLabel>
                  <Field
                      name="toDate"
                      component={DatePicker}
                      disabledBefore={disabledBefore}
                      fluid
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="treaterSelection"
                      component={CheckBox}
                      label={getMessage("dayStatistic.treaterSelection")}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="treaterId"
                      component={Select}
                      options={treaters}
                      disabled={treaterDisabled}
                      fluid
                  />
                </SearchField>
              </SearchGroup>
              <SearchGroup>
                <Header size="tiny">{getMessage("automaticBilling.tariffSelection")}</Header>
                <TableContainer>
                  {this.renderTariffsTable(form, values as ExtendedDayStatisticRequest, tariffs)}
                </TableContainer>
                <SearchField className="marginTop">
                  <Field
                      name="inactiveTariffs"
                      component={CheckBox}
                      label={getMessage("dayStatistic.includingInactive")}
                  />
                </SearchField>
              </SearchGroup>
              {values.option === DayStatisticsOptions.PROCEDURES_PROTOCOL &&
              <SearchGroup>
                <Header size="tiny">{getMessage("dayStatistic.sortBy")}</Header>
                <SearchField>
                  <Field
                      name="sortBy"
                      component={Radio}
                      radioDefinition={time}
                  />
                </SearchField>
                <SearchField>
                  <Field
                      name="sortBy"
                      component={Radio}
                      radioDefinition={patientName}
                  />
                </SearchField>
              </SearchGroup>
              }
              <SearchButtonContainer>
                <Button
                    type="button" primary compact
                    onClick={() => this.getStatistics(values)}
                    disabled={hasValidationErrors || disableSearch}>
                  {getMessage(btnMessage)}
                </Button>
              </SearchButtonContainer>
            </SearchOptionsContainer>
          </StyledContainer>
          <FormSpy subscription={{values: true}} onChange={formState => this.handleChange(formState, form)}/>
        </StyledForm>
    )
  };

  selectHeaderColumn = (form: FormApi, values: ExtendedDayStatisticRequest, tariffs: Array<TariffDto>) => {

    const countSelectedTariffs = values.tariffIds.length;
    const indeterminate = countSelectedTariffs > 0 && countSelectedTariffs < tariffs.length;
    const noSelectedTariffs = countSelectedTariffs === 0 && tariffs.length > 0;
    const allTariffsSelected = tariffs.length > 0 && countSelectedTariffs === tariffs.length;

    return (
        <CheckboxSc
            checked={countSelectedTariffs > 0}
            indeterminate={indeterminate}
            onChange={
              () => {
                if (noSelectedTariffs || indeterminate) {
                  const selectedTariffs = tariffs.map(tariff => tariff.id);
                  form.change("tariffIds", selectedTariffs);
                }

                if (allTariffsSelected) {
                  form.change("tariffIds", []);
                }

              }
            }
            disabled={tariffs.length === 0}
        />
    )
  };

  tariffsRowGetter = (tariffs: Array<TariffDto>) => ({index}: any) => {

    if (tariffs[index]) {
      Object.assign(tariffs[index], {index: index + 1});

      return tariffs[index];
    }

    return {
      index: index + 1
    }
  };

  tariffsRowRenderer = (form: FormApi, values: ExtendedDayStatisticRequest) => ({
                                                                                  className,
                                                                                  columns,
                                                                                  index,
                                                                                  key,
                                                                                  style,
                                                                                  rowData
                                                                                }: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    let styles = {...style, userSelect: "none"};

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => this.selectTariff(values, form, rowData.id)}
        >
          {columns}
        </div>
    );
  };

  tariffSelectCellRenderer = (data: any, values: ExtendedDayStatisticRequest, form: FormApi) => {

    const {rowData} = data;
    const {id} = rowData;

    const tariffSelected = values.tariffIds.includes(id);

    return (
        <CheckboxSc
            checked={tariffSelected}
            onChange={() => this.selectTariff(values, form, id)}
        />
    )
  };

  tariffDescriptionCellRenderer = (data: any) => {

    return <div>{data.rowData.description}</div>
  };

  renderTariffsTable = (form: FormApi, values: ExtendedDayStatisticRequest, tariffs: Array<TariffDto>): React.ReactNode => {

    const {getMessage} = this.props;

    let tariffSelection: Array<TariffDto> = tariffs.filter(t => t.active);

    if (values.inactiveTariffs) {
      tariffSelection = tariffs;
    }

    return (
        <StyledTable
            rowGetter={this.tariffsRowGetter(tariffSelection)}
            rowRenderer={this.tariffsRowRenderer(form, values)}
            rowCount={tariffSelection.length}
            columns={[
              {
                width: 30,
                label: this.selectHeaderColumn(form, values, tariffSelection),
                dataKey: "id",
                cellRenderer: (data: any) => this.tariffSelectCellRenderer(data, values, form)
              },
              {
                width: 180,
                label: getMessage("description"),
                dataKey: "description",
                cellRenderer: this.tariffDescriptionCellRenderer
              }
            ]}
        />
    )
  };

}

export default withAuthContext(withMessages(DayStatisticSearchView));
